import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePageHeader } from '../context/PageHeaderContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, HelpCircle, Mail, Phone, Building2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * HelpButtonsPage — admin-only config page (header HelpCircle icon
 * navigates here). Surfaces the three dynamic-button targets used by
 * the AuthPage "I can't provide both" fallback:
 *
 *   1. Email Supervisor (per state) — pick which active supervisor
 *      receives identity-help emails for affiliates/leads in that state.
 *   2. Contact Leadership — pick a contact kind (email, phone, none),
 *      with a customizable button label.
 *   3. Email Support — customize the button label and support email.
 */
export default function HelpButtonsPage() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageHeader({ title: 'Help Buttons', back: '/admin/security-requirements' }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get('/admin/help-buttons/config'),
        api.get('/admin/help-buttons/states'),
      ]);
      setCfg(c.data);
      setStates(s.data?.states || []);
    } catch (e) {
      toast.error('Failed to load Help Buttons config');
    } finally { setLoading(false); }
  };
  useEffect(() => { if (user?.role === 'admin') load(); /* eslint-disable-next-line */ }, [user?.role]);

  if (user?.role !== 'admin') {
    return <div className="p-6 text-center text-sm text-muted-foreground">Admin only</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 pb-24" data-testid="help-buttons-page">
      <div className="max-w-2xl mx-auto space-y-4">
        <p className="text-xs text-muted-foreground">
          Controls the dynamic button on the forgot-password "I can't provide both" screen.
        </p>

        {loading ? (
          <p className="text-xs text-center py-6 text-muted-foreground">Loading…</p>
        ) : !cfg ? null : (
          <>
            <StateSupervisorSection states={states} onChange={load} api={api} />
            <LeadershipSection cfg={cfg.leadership} onChange={load} api={api} />
            <SupportSection cfg={cfg.support} onChange={load} api={api} />
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────── State Supervisor Section ───────────────────

function StateSupervisorSection({ states, onChange, api }) {
  const [savingFor, setSavingFor] = useState(null);

  const setPin = async (state_code, supervisor_user_id) => {
    setSavingFor(state_code);
    try {
      await api.put('/admin/help-buttons/state-supervisor', {
        state: state_code, supervisor_user_id: supervisor_user_id || null,
      });
      toast.success('Saved');
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSavingFor(null); }
  };

  return (
    <Card data-testid="help-buttons-state-supervisor-section">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-[#586D52]" />
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email Supervisor</p>
        </div>
        <p className="text-[11px] text-muted-foreground">
          For each state, choose which supervisor receives identity-help emails from affiliates and leads in that state. If a state is left blank, the first active supervisor in that state is used automatically.
        </p>
        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1" data-testid="help-state-list">
          {states.map((s) => (
            <div key={s.state_code} className="flex items-center gap-2 text-xs">
              <span className="w-10 font-mono font-bold">{s.state_code}</span>
              <span className="flex-1 truncate text-muted-foreground">{s.state_name}</span>
              <Select
                value={s.pinned_id || ''}
                onValueChange={(v) => setPin(s.state_code, v === '__clear__' ? null : v)}
                disabled={savingFor === s.state_code || s.supervisors.length === 0}
              >
                <SelectTrigger className="h-7 text-xs w-44" data-testid={`help-state-${s.state_code}-select`}>
                  <SelectValue placeholder={s.supervisors.length ? 'Auto (first)' : 'No supervisors'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__clear__">Auto (first supervisor)</SelectItem>
                  {s.supervisors.map((sup) => (
                    <SelectItem key={sup.id} value={sup.id}>{sup.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────── Leadership Section ───────────────────

function LeadershipSection({ cfg, onChange, api }) {
  const [kind, setKind] = useState(cfg?.kind || 'none');
  const [label, setLabel] = useState(cfg?.label || 'Contact Leadership');
  const [value, setValue] = useState(cfg?.value || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setKind(cfg?.kind || 'none');
    setLabel(cfg?.label || 'Contact Leadership');
    setValue(cfg?.value || '');
  }, [cfg]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/help-buttons/leadership', { kind, label, value: value || null });
      toast.success('Saved');
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="help-buttons-leadership-section">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          {kind === 'email' ? <Mail className="w-4 h-4 text-[#586D52]" /> : kind === 'phone' ? <Phone className="w-4 h-4 text-[#586D52]" /> : <Building2 className="w-4 h-4 text-[#586D52]" />}
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Contact Leadership</p>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Shown to supervisors, admins, and special users on the forgot-password fallback.
        </p>
        <div className="grid grid-cols-3 gap-2 text-xs">
          {['none', 'email', 'phone'].map((k) => (
            <button
              key={k}
              onClick={() => setKind(k)}
              className={`px-2 py-1.5 rounded-md font-medium ${
                kind === k ? 'bg-[#586D52] text-white' : 'bg-muted text-muted-foreground'
              }`}
              data-testid={`leadership-kind-${k}`}
            >
              {k === 'none' ? 'No action' : k === 'email' ? 'Email' : 'Phone'}
            </button>
          ))}
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px]">Button label</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} className="text-xs h-8" data-testid="leadership-label-input" />
        </div>
        {kind !== 'none' && (
          <div className="space-y-1.5">
            <Label className="text-[11px]">{kind === 'email' ? 'Email address' : 'Phone number'}</Label>
            <Input
              type={kind === 'email' ? 'email' : 'tel'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={kind === 'email' ? 'leadership@collectiveorb.com' : '+1 555 555 5555'}
              className="text-xs h-8"
              data-testid="leadership-value-input"
            />
          </div>
        )}
        <Button size="sm" className="w-full h-8 text-xs bg-[#586D52] hover:bg-[#4a5d46]" onClick={save} disabled={saving} data-testid="leadership-save">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ─────────────────── Support Section ───────────────────

function SupportSection({ cfg, onChange, api }) {
  const [label, setLabel] = useState(cfg?.label || 'Email Support');
  const [email, setEmail] = useState(cfg?.email || 'upliftsupport@collectiveorb.com');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLabel(cfg?.label || 'Email Support');
    setEmail(cfg?.email || 'upliftsupport@collectiveorb.com');
  }, [cfg]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/help-buttons/support', { label, email });
      toast.success('Saved');
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid="help-buttons-support-section">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#586D52]" />
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email Support</p>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Shown to users not yet registered in the system. Always email-based.
        </p>
        <div className="space-y-1.5">
          <Label className="text-[11px]">Button label</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} className="text-xs h-8" data-testid="support-label-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px]">Email address</Label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="text-xs h-8" data-testid="support-email-input" />
        </div>
        <Button size="sm" className="w-full h-8 text-xs bg-[#586D52] hover:bg-[#4a5d46]" onClick={save} disabled={saving} data-testid="support-save">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  );
}
